/**
 * Build the Compare version list for a docx-native DEAL DOCUMENT.
 *
 * The template equivalent compares "current draft" vs "last published". A deal
 * has no publish step, so the versions here are the CURRENT body plus any
 * resolvable historical snapshot.
 *
 * IMPORTANT — why `document_revisions` are NOT listed: those rows store
 * CKEditor `diffData` (serialized DOM deletions/insertions between versions),
 * NOT a document body. Verified against real data: all 195 local deal revisions
 * carry `diffData` and none carry a body, so there is nothing to hand the
 * redline engine. Listing them would produce entries that can never load.
 *
 * What IS resolvable today is `cke_original_body` — the pre-migration CKEditor
 * copy retained on every migrated document — which gives a genuine
 * "before/after migration" comparison. Named snapshots can be added here as
 * soon as the deal side starts storing real bodies.
 *
 * Mirrors the template's builder so both feed the same `CompareVersionsPanel`
 * and the same redline engine.
 */

import type { CompareVersion } from './compareTypes';

/** A deal revision as returned by `getDocumentRevisions`. */
export interface DealRevisionLike {
  id: string | number;
  createdAt?: string | null;
  name?: string | null;
  authorName?: string | null;
  /** False for diff-only rows that carry no body — those can never load, so
   *  they are filtered out rather than offered as dead entries. */
  hasSnapshot?: boolean | null;
  /** 'docx' | 'html'. Legacy CKEditor rows default to html. */
  snapshotFormat?: string | null;
  /** The event that produced the version (possession_transfer, prepare_to_sign,
   *  approval, manual…) — used to give unnamed auto-versions a sensible label. */
  event?: string | null;
}

/** Decode a base64 `.docx` body to bytes. */
function base64DocxToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Is this string base64-encoded .docx (i.e. ZIP) bytes?
 *
 * `UEsDBA` is base64 for `PK\x03\x04`, the ZIP local-file-header magic that
 * opens every .docx. Trusting `body_storage_format` alone was not enough: a
 * legacy save could stamp `html` onto a body it never wrote (see
 * UpdateDocumentMutation), and snapshots taken in that window carry the wrong
 * marker forever. The bytes cannot lie, so ask them — this repairs already
 * mislabeled rows without a data migration.
 */
function looksLikeBase64Docx(body: string): boolean {
  return body.trimStart().startsWith('UEsDBA');
}

/**
 * Resolve a stored body hash (`{ body, body_storage_format }`) to .docx bytes,
 * converting legacy CKEditor HTML on demand. Same contract the template uses.
 */
export async function dealBodyToDocxBytes(
  bodyHash: Record<string, any> | undefined | null
): Promise<Uint8Array | null> {
  const body: string | undefined = bodyHash?.body;
  if (!body) return null;

  if (bodyHash?.body_storage_format === 'docx' || looksLikeBase64Docx(body)) {
    try {
      return base64DocxToBytes(body);
    } catch {
      return null;
    }
  }

  // Legacy fallback ONLY. Every version is now stored as .docx (the backfill
  // converts reconstructed CKE revisions server-side), so Compare is 1:1 docx
  // and this branch should never run in practice. If it does, a version slipped
  // through as html — log it so the gap is visible rather than silently slow.
  // eslint-disable-next-line no-console
  // Legacy HTML → convert on the fly. This is EXPECTED for the "Before
  // migration" entry (the retained CKE original is html); it is unexpected for
  // any stored revision (those are all docx post-backfill).
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.info('[compare] converting a legacy html version body to docx on the fly (expected for the pre-migration original).');
  }
  let html = body;
  try {
    const decoded = atob(body);
    if (/<[a-z!]/i.test(decoded)) html = decoded;
  } catch {
    /* already raw html */
  }
  const { convertHtmlToDocx } = await import('../../../../utils/docx-migration-v2');
  const { buffer } = await convertHtmlToDocx(html, window.DOMParser);
  return buffer;
}

/**
 * Turn a list of saved revisions into CompareVersion entries — shared by the
 * deal and template compare builders (same relational `document_revisions`,
 * same shape). Diff-only rows (`hasSnapshot === false`) are excluded; bodies
 * load lazily through `loadRevisionBody` + the given bytes resolver.
 */
export function buildRevisionVersions(
  revisions: readonly DealRevisionLike[],
  loadRevisionBody: ((revisionId: string) => Promise<Record<string, any> | null>) | undefined,
  toBytes: (bodyHash: Record<string, any> | undefined | null) => Promise<Uint8Array | null>
): CompareVersion[] {
  const sorted = [...revisions]
    .filter((r) => r.hasSnapshot !== false)
    .sort((a, b) => {
      const at = a.createdAt ? Date.parse(a.createdAt) : 0;
      const bt = b.createdAt ? Date.parse(b.createdAt) : 0;
      return bt - at; // newest first
    });

  return sorted.map((rev) => {
    const id = String(rev.id);
    return {
      key: `revision-${id}`,
      // Prefer an explicit name, then a friendly event label, then a
      // DATE-based label. Never fall back to the raw revision id ("Version
      // 1032") — that id is an internal DB key and means nothing to a user.
      label:
        rev.name?.trim() ||
        eventLabel(rev.event) ||
        (formatWhen(rev.createdAt) ? `Version — ${formatWhen(rev.createdAt)}` : `Version ${id}`),
      // Date leads the sublabel — the most valuable orientation cue; author follows.
      sublabel: [formatWhen(rev.createdAt), rev.authorName].filter(Boolean).join(' · ') || undefined,
      getBytes: async () => {
        if (!loadRevisionBody) return null;
        const body = await loadRevisionBody(id);
        return toBytes(body);
      },
    };
  });
}

/** Friendly label for an auto-capture event when a revision has no name. */
function eventLabel(event?: string | null): string | undefined {
  switch (event) {
    case 'possession_transfer':
      return 'Possession transferred';
    case 'prepare_to_sign':
      return 'Sent for signing';
    case 'approval':
      return 'Approved';
    case 'sent_by_email':
      return 'Sent by email';
    case 'publish':
      return 'Published';
    case 'import':
      return 'Imported new version';
    default:
      return undefined;
  }
}

function formatWhen(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? undefined
    : d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

/**
 * @param currentBody  The deal's live body (`documents.document`).
 * @param revisions    Saved revisions, any order — sorted newest-first here.
 * @param loadRevisionBody  Fetches a revision's stored body hash on demand, so
 *                          opening the panel doesn't download every revision.
 */
export function buildDealCompareVersions(
  currentBody: Record<string, any> | undefined | null,
  revisions: readonly DealRevisionLike[] = [],
  loadRevisionBody?: (revisionId: string) => Promise<Record<string, any> | null>
): CompareVersion[] {
  const versions: CompareVersion[] = [
    {
      key: 'current',
      label: 'Current version',
      getBytes: () => dealBodyToDocxBytes(currentBody),
    },
  ];

  // The retained pre-migration CKEditor body — a genuinely resolvable second
  // version, converted on demand by `dealBodyToDocxBytes`'s legacy-HTML branch.
  if (currentBody?.cke_original_body) {
    versions.push({
      key: 'pre-migration',
      label: 'Before migration',
      sublabel: 'Original CKEditor version',
      getBytes: () =>
        dealBodyToDocxBytes({
          body: currentBody.cke_original_body,
          body_storage_format: currentBody.cke_original_format ?? 'html',
        }),
    });
  }

  // Only revisions with a real body can be compared. `hasSnapshot === false`
  // means diff-only; `undefined` means the caller didn't ask for the field, so
  // keep it (the loader will resolve or return null).
  versions.push(
    ...buildRevisionVersions(revisions, loadRevisionBody, dealBodyToDocxBytes)
  );

  return versions;
}
